// lib/firebase.ts
'use client';

import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  Auth // Import Auth type
} from 'firebase/auth';
import {
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
  Firestore // Import Firestore type
} from 'firebase/firestore';
import {
  getStorage,
  FirebaseStorage // Import FirebaseStorage type
} from 'firebase/storage';

// Helper to sanitize env values (remove CR/LF and trim)
const clean = (v?: string) => (v ? v.replace(/\r?\n/g, '').trim() : undefined);

// Default Firebase configuration for development
const devConfig = {
  apiKey: "AIzaSyDWVM8PvcWD4nAkpsI7FuDKCvpp_PEnPlU",
  authDomain: "solynta-academy.firebaseapp.com",
  projectId: "solynta-academy",
  storageBucket: "solynta-academy.firebasestorage.app",
  messagingSenderId: "914922463191",
  appId: "1:914922463191:web:b6e9c737dba77a26643592",
  measurementId: "G-ZVC7R06Y33"
};

// Firebase configuration - try environment variables first, then fallback to dev config
const firebaseConfig = {
  apiKey: clean(process.env.NEXT_PUBLIC_FIREBASE_API_KEY) || devConfig.apiKey,
  authDomain: clean(process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN) || devConfig.authDomain,
  projectId: clean(process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID) || devConfig.projectId,
  storageBucket: clean(process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET) || devConfig.storageBucket,
  messagingSenderId: clean(process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID) || devConfig.messagingSenderId,
  appId: clean(process.env.NEXT_PUBLIC_FIREBASE_APP_ID) || devConfig.appId,
  measurementId: clean(process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID) || devConfig.measurementId
};

console.log('Using Firebase config with project ID:', firebaseConfig.projectId);

// Initialize Firebase app (Singleton pattern)
const app: FirebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// Initialize services - these will be initialized client-side
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;

// Check if running in a browser environment
if (typeof window !== 'undefined') {
  // Initialize Auth
  auth = getAuth(app);
  const persistenceMode = (process.env.NEXT_PUBLIC_AUTH_PERSISTENCE || 'local').toLowerCase();
  const persistence = persistenceMode === 'session' ? browserSessionPersistence : browserLocalPersistence;
  console.log(`[Firebase] Setting auth persistence: ${persistenceMode}`);
  setPersistence(auth, persistence)
    .catch((error) => console.error("Auth persistence error:", error));

  // Initialize Firestore with error handling
  try {
    db = initializeFirestore(app, {
      cacheSizeBytes: CACHE_SIZE_UNLIMITED,
      experimentalForceLongPolling: true, // Consider if still needed, might impact performance/cost
      ignoreUndefinedProperties: true, // Useful for avoiding errors with undefined values
    });
    console.log('Firestore initialized successfully');
  } catch (error) {
    console.error('Error initializing Firestore:', error);
    // Fallback to default Firestore initialization
    try {
      const { getFirestore } = require('firebase/firestore');
      db = getFirestore(app);
      console.log('Firestore initialized with fallback method');
    } catch (fallbackError) {
      console.error('Firestore fallback initialization failed:', fallbackError);
    }
  }

  // Initialize Storage
  storage = getStorage(app);

  // Connect to emulators in development if configured
  if (process.env.NODE_ENV === 'development' &&
      process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === 'true') {
    console.log("Connecting to Firebase Emulators...");
    // Use dynamic imports for emulator functions to potentially reduce bundle size
    import('firebase/auth').then(({ connectAuthEmulator }) => {
      try {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        console.log("Auth Emulator connected to http://localhost:9099");
      } catch (error) {
        console.error("Error connecting to Auth Emulator:", error);
      }
    });

    import('firebase/firestore').then(({ connectFirestoreEmulator }) => {
      try {
        connectFirestoreEmulator(db, 'localhost', 8080);
        console.log("Firestore Emulator connected to localhost:8080");
      } catch (error) {
        console.error("Error connecting to Firestore Emulator:", error);
      }
    });

    import('firebase/storage').then(({ connectStorageEmulator }) => {
      try {
        connectStorageEmulator(storage, 'localhost', 9199);
        console.log("Storage Emulator connected to localhost:9199");
      } catch (error) {
        console.error("Error connecting to Storage Emulator:", error);
      }
    });
  }
} else {
  // Provide non-functional placeholders for SSR/server environments
  // This prevents errors during import but these services won't work server-side
  // Assigning {} as Type might cause issues if methods are called server-side.
  // A more robust approach might involve providing mock implementations or
  // ensuring these exports are only used in client components.
  auth = {} as Auth;
  db = {} as Firestore;
  storage = {} as FirebaseStorage;
}

export { app, auth, db, storage };